// Propriedades do passo selecionado — Fase 10.
//
// Os campos são desenhados a partir do catálogo (`catalogoNos.ts`), não de um
// `switch` por tipo. Um `switch` aqui significaria que acrescentar um tipo de
// passo exige lembrar deste arquivo — e o esquecimento não dá erro: o passo
// aparece no canvas com o painel em branco, e quem monta acha que o passo não
// tem configuração.

import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { CAMPOS_INTERPOLAVEIS, OPERADORES, passo } from '../catalogoNos';
import type { CampoConfig } from '../catalogoNos';
import type { NoFluxo } from '../tipos';
import type { Problema } from '../validarFluxo';
import { BotaoApagarPasso } from './Canvas';

interface Props {
  no: NoFluxo | null;
  problemas: Problema[];
  tags: { id: string; nome: string; cor: string }[];
  membros: { id: string; nome: string }[];
  ehInicial: boolean;
  somenteLeitura: boolean;
  onMudarChave: (nova: string) => void;
  onMudarConfig: (config: Record<string, unknown>) => void;
  onDefinirInicial: () => void;
  onApagar: () => void;
}

const rotuloClasse = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400';
const campoClasse =
  'mt-1 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-800 outline-none transition-colors focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100';

export default function PainelPropriedades({
  no, problemas, tags, membros, ehInicial, somenteLeitura,
  onMudarChave, onMudarConfig, onDefinirInicial, onApagar,
}: Props) {
  if (!no) {
    return (
      <aside className="flex h-full flex-col items-center justify-center rounded-2xl bg-white p-6 text-center ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
        <Info size={20} className="mb-2 text-gray-300 dark:text-slate-600" />
        <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Nenhum passo selecionado</p>
        <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
          Clique em um passo no canvas para configurar.
        </p>
      </aside>
    );
  }

  const definicao = passo(no.tipo);
  const config = no.config ?? {};
  const doPasso = problemas.filter((p) => p.noKey === no.node_key);

  const mudar = (chave: string, valor: unknown) => onMudarConfig({ ...config, [chave]: valor });

  const desenharCampo = (campo: CampoConfig) => {
    const valor = config[campo.chave];

    if (campo.tipo === 'condicao') {
      const cond = (valor ?? {}) as { campo?: string; operador?: string; valor?: string };
      const op = OPERADORES.find((o) => o.valor === cond.operador);
      return (
        <div className="space-y-2">
          <div>
            <label className={rotuloClasse}>Campo</label>
            <input
              value={cond.campo ?? ''}
              disabled={somenteLeitura}
              onChange={(e) => mudar('condicao', { ...cond, campo: e.target.value })}
              placeholder="resposta"
              className={campoClasse}
            />
          </div>
          <div>
            <label className={rotuloClasse}>Comparação</label>
            <select
              value={cond.operador ?? ''}
              disabled={somenteLeitura}
              onChange={(e) => mudar('condicao', { ...cond, operador: e.target.value })}
              className={campoClasse}
            >
              <option value="">Escolha…</option>
              {OPERADORES.map((o) => (
                <option key={o.valor} value={o.valor}>{o.rotulo}</option>
              ))}
            </select>
          </div>
          {/* O campo de valor some quando o operador não usa valor ("está
              vazio"). Deixá-lo aparecendo faria a pessoa preencher um valor
              que o motor ignora — e acreditar que ele conta. */}
          {(!op || op.precisaValor) && (
            <div>
              <label className={rotuloClasse}>Valor</label>
              <input
                value={String(cond.valor ?? '')}
                disabled={somenteLeitura}
                onChange={(e) => mudar('condicao', { ...cond, valor: e.target.value })}
                className={campoClasse}
              />
            </div>
          )}
        </div>
      );
    }

    if (campo.tipo === 'tag') {
      return (
        <select
          value={String(valor ?? '')}
          disabled={somenteLeitura}
          onChange={(e) => mudar(campo.chave, e.target.value)}
          className={campoClasse}
        >
          <option value="">Escolha uma etiqueta…</option>
          {tags.map((t) => (
            <option key={t.id} value={t.id}>{t.nome}</option>
          ))}
        </select>
      );
    }

    if (campo.tipo === 'usuario') {
      return (
        <select
          value={String(valor ?? '')}
          disabled={somenteLeitura}
          onChange={(e) => mudar(campo.chave, e.target.value)}
          className={campoClasse}
        >
          <option value="">Escolha uma pessoa…</option>
          {membros.map((m) => (
            <option key={m.id} value={m.id}>{m.nome}</option>
          ))}
        </select>
      );
    }

    if (campo.tipo === 'escolha') {
      return (
        <select
          value={String(valor ?? campo.padrao ?? '')}
          disabled={somenteLeitura}
          onChange={(e) => mudar(campo.chave, e.target.value)}
          className={campoClasse}
        >
          {(campo.opcoes ?? []).map((o) => (
            <option key={o.valor} value={o.valor}>{o.rotulo}</option>
          ))}
        </select>
      );
    }

    if (campo.tipo === 'texto_longo') {
      return (
        <>
          <textarea
            value={String(valor ?? '')}
            disabled={somenteLeitura}
            rows={5}
            onChange={(e) => mudar(campo.chave, e.target.value)}
            className={`${campoClasse} resize-y`}
          />
          {!somenteLeitura && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {CAMPOS_INTERPOLAVEIS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => mudar(campo.chave, String(valor ?? '') + `{{${c}}}`)}
                  className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-primary/10 hover:text-primary dark:bg-slate-700 dark:text-slate-300"
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </>
      );
    }

    if (campo.tipo === 'numero') {
      return (
        <input
          type="number"
          min={1}
          value={String(valor ?? campo.padrao ?? '')}
          disabled={somenteLeitura}
          onChange={(e) => mudar(campo.chave, e.target.value === '' ? '' : Number(e.target.value))}
          className={campoClasse}
        />
      );
    }

    return (
      <input
        value={String(valor ?? '')}
        disabled={somenteLeitura}
        onChange={(e) => mudar(campo.chave, e.target.value)}
        className={campoClasse}
      />
    );
  };

  return (
    <aside className="flex h-full flex-col overflow-y-auto rounded-2xl bg-white ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
      <header className="sticky top-0 z-10 border-b border-gray-100 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${definicao?.cor ?? 'bg-gray-300'}`} />
          <p className="text-sm font-bold text-gray-800 dark:text-white">{definicao?.rotulo ?? no.tipo}</p>
        </div>
        <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-slate-400">
          {definicao?.resumo ?? 'Tipo de passo que o editor não reconhece.'}
        </p>
      </header>

      <div className="flex-1 space-y-4 px-4 py-3">
        <div>
          <label className={rotuloClasse}>Nome do passo</label>
          <input
            value={no.node_key}
            disabled={somenteLeitura}
            onChange={(e) => onMudarChave(e.target.value)}
            className={campoClasse}
          />
          <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-slate-500">
            É o nome que aparece na trilha de execução. Renomear reaponta as ligações junto.
          </p>
        </div>

        {(definicao?.campos ?? []).map((campo) => (
          <div key={campo.chave}>
            <label className={rotuloClasse}>
              {campo.rotulo}
              {campo.obrigatorio && <span className="ml-1 text-red-400">*</span>}
            </label>
            {desenharCampo(campo)}
            {campo.ajuda && (
              <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-slate-500">{campo.ajuda}</p>
            )}
          </div>
        ))}

        {doPasso.length > 0 && (
          <div className="space-y-1.5">
            {doPasso.map((p, i) => (
              <p
                key={i}
                className={`flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] leading-snug ${
                  p.nivel === 'erro'
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                    : 'bg-secondary/15 text-[#8A5D0F] dark:bg-secondary/10 dark:text-secondary'
                }`}
              >
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                {p.mensagem}
              </p>
            ))}
          </div>
        )}
      </div>

      {!somenteLeitura && (
        <footer className="space-y-2 border-t border-gray-100 px-4 py-3 dark:border-slate-700">
          {!ehInicial && (
            <button
              onClick={onDefinirInicial}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-600 dark:text-slate-300"
            >
              Começar o fluxo por este passo
            </button>
          )}
          <BotaoApagarPasso onApagar={onApagar} />
        </footer>
      )}
    </aside>
  );
}
